import React, { useCallback, useState, useEffect } from "react";
import {loadStripe} from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
}  from '@stripe/react-stripe-js';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate
} from "react-router-dom";
import Home from "./Home";
import SignUp from "./SignUp";
import SignIn from "./SignIn";  
import VerifyCode from "./VerifyCode";
import PasswordReset from "./PasswordReset";
import ProductDetail from "./ProductDetail";
import Orders from "./Orders";
import { CartProvider, useCart } from "./CartContext";
import { saveOrder } from "./Orders";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function getCustomerEmailFromSession() {
  try {
    const idToken = sessionStorage.getItem('idToken');
    if (!idToken) return null;
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return payload.email || null;
  } catch {
    return null;
  }
}

const CheckoutForm = () => {
  const { lineItems, clearCart } = useCart();

  const fetchClientSecret = useCallback(() => {
    const customerEmail = getCustomerEmailFromSession();
    const body = { line_items: lineItems };
    if (customerEmail) body.customer_email = customerEmail;

    return fetch(process.env.REACT_APP_CREATE_CHECKOUT_SESSION_URL, {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((data) => {
        // Lambda returns { statusCode, body } — parse if needed
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        return parsed.clientSecret;
      });
  }, [lineItems]);

  const options = { fetchClientSecret, onComplete: () => { clearCart(); } };

  return (
    <div id="checkout">
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}

const Return = ({ cartItems }) => {
  const [status, setStatus] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const savedRef = React.useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (!sessionId) return;

    fetch(`${process.env.REACT_APP_SESSION_STATUS_URL}?session_id=${sessionId}`)
      .then((res) => res.json())
      .then((data) => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setStatus(parsed.status);
        setCustomerEmail(parsed.customer_email || '');
        if (parsed.status === 'complete' && !savedRef.current) {
          savedRef.current = true;
          saveOrder({
            sessionId,
            date: new Date().toISOString(),
            customerEmail: parsed.customer_email,
            amountTotal: parsed.amount_total,
            currency: parsed.currency,
            items: cartItems || [],
          });
        }
      })
      .catch((err) => console.error('session-status error:', err));
  }, [cartItems]);

  if (status === 'open') {
    return <Navigate to="/checkout" />;
  }

  if (status === 'complete') {
    return (
      <div className="container text-center mt-6">
        <i className="material-symbols-rounded text-success" style={{ fontSize: "4rem" }}>check_circle</i>
        <h3 className="mt-3 font-weight-bold">Order Confirmed!</h3>
        <p className="text-muted">
          We appreciate your business!{customerEmail && <> A confirmation email will be sent to <strong>{customerEmail}</strong>.</>}
        </p>
        <div className="d-flex gap-3 justify-content-center mt-4">
          <a href="/orders" className="btn bg-gradient-dark">View Orders</a>
          <a href="/" className="btn btn-outline-dark">Continue Shopping</a>
        </div>
      </div>
    );
  }

  return (
    <div className="container text-center mt-6">
      <div className="spinner-border text-dark" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 text-muted">Confirming your order...</p>
    </div>
  );
}

// const Home = () => {
//   const [status, setStatus] = useState(null);
//   const [customerEmail, setCustomerEmail] = useState('');
//   console.log("returning homie");

//   useEffect(() => {
//     const queryString = window.location.search;
//     const urlParams = new URLSearchParams(queryString);
//     const sessionId = urlParams.get('session_id');
//     console.log("in the use effect");

//     fetch(`https://zzs7owomj1.execute-api.us-east-1.amazonaws.com/default/sessionStatus?session_id=${sessionId}`)
//       .then((res) => res.json())
//       .then((data) => {
//         setStatus(data.status);
//         setCustomerEmail(data.customer_email);
//       });
//   }, []);

//   if (status === 'open') {
//     console.log("this thing is open");
//     return (
//       <Navigate to="/checkout" />
//     )
//   }

//   if (status === 'complete') {
//     console.log("this thing is complete");
//     return (
//       <section id="success">
//         <p>
//           We appreciate your business! A confirmation email will be sent to {customerEmail}.

//           If you have any questions, please email <a href="mailto:orders@example.com">orders@example.com</a>.
//         </p>
//       </section>
//     )
//   }

//   return null;
// }

function AppRoutes() {
  const [products, setProducts] = useState([]);
  const { items } = useCart();

  useEffect(() => {
    fetch(process.env.REACT_APP_GET_PRODUCTS_URL)
      .then((res) => res.json())
      .then((data) => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setProducts(parsed.products || []);
      })
      .catch(() => {});
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/the-latino-startup-merch" element={<Home pageFilter="latino-startup" title="The Latino Startup Merch" />} />
      <Route path="/product/:id" element={<ProductDetail products={products} />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/checkout" element={<CheckoutForm />} />
      <Route path="/return" element={<Return cartItems={items} />} />
      <Route path="/signUp" element={<SignUp />} />
      <Route path="/signIn" element={<SignIn />} />
      <Route path="/verifyCode" element={<VerifyCode />} />
      <Route path="/passwordReset" element={<PasswordReset />} />
    </Routes>
  );
}

function TestFrida() {
  return (
    <CartProvider>
      <Router>
        <AppRoutes />
      </Router>
    </CartProvider>
  );
}
  
  export default TestFrida;